import { useState } from "react";
import { Button } from "@/components/Base/Button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/Base/Dialog";
import { Tag } from "@/types/v2/Tags";
import { DEFAULT_TAG_COLOR, TAG_COLORS } from "@/lib/const";
import { getTextColor } from "@/lib/utils";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/Base/Form";
import { Input } from "@/components/Base/Input";
import { Icon } from "@/components/Common/Icon";

// Define the form schema using zod
const formSchema = z.object({
  label: z.string().min(1, { message: "Tag label is required" }),
  customColor: z.string().min(1, { message: "Tag color is required" }),
});

type FormValues = z.infer<typeof formSchema>;

export interface EditTagDialogProps {
  tag: Tag;
  onSave?: (tag: Tag) => void;
  onDelete?: () => void;
  onCancel?: () => void;
}

export const EditTagDialog = ({
  tag,
  onSave,
  onDelete,
  onCancel,
}: EditTagDialogProps) => {
  const [open, setOpen] = useState(false);

  // Initialize form with react-hook-form and zod validation
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      label: tag.label,
      customColor: tag.customColor || "#CCCCCC",
    },
  });

  const handleSubmit = (values: FormValues) => {
    if (onSave) {
      onSave({
        ...tag,
        label: values.label,
        customColor: values.customColor,
      });
    }
    setOpen(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete();
    }
    setOpen(false);
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    form.reset({
      label: tag.label,
      customColor: tag.customColor || DEFAULT_TAG_COLOR,
    });
    setOpen(false);
  };

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="ml-2"
          onClick={() => {
            setOpen(true);
          }}
        >
          <Icon name={"editSquare"} className={"text-foreground"} />
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[350px] p-0">
        <div
          className="flex h-[60px] w-full items-center justify-center rounded-sm border"
          style={{
            backgroundColor: form.watch("customColor"),
            color: getTextColor(form.watch("customColor")),
          }}
        >
          <DialogTitle>{form.watch("label")}</DialogTitle>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="space-y-4"
          >
            <div className="px-5 py-2">
              <FormField
                control={form.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="tagLabel">Tag Label</FormLabel>
                    <FormControl>
                      <Input
                        id="tagLabel"
                        type="text"
                        placeholder="Tag label"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="px-5 py-2">
              <FormField
                control={form.control}
                name="customColor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel htmlFor="tagColor">Tag Color</FormLabel>
                    <FormControl>
                      <div className="mt-2 grid grid-cols-4 items-center gap-2">
                        {TAG_COLORS.map((tagColor) => (
                          <div
                            key={tagColor}
                            className={`mx-auto h-8 w-8 cursor-pointer rounded-full border ${
                              field.value === tagColor
                                ? "ring-2 ring-primary ring-offset-2"
                                : ""
                            }`}
                            style={{ backgroundColor: tagColor }}
                            onClick={() =>
                              form.setValue("customColor", tagColor, {
                                shouldValidate: true,
                              })
                            }
                          />
                        ))}
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col items-center space-y-3 px-5 py-4">
              <Button
                type="submit"
                variant="accent"
                className="h-[40px] w-full"
              >
                Save
              </Button>
              <Button
                type="button"
                variant="destructive"
                className="h-[40px] w-full gap-2"
                onClick={handleDelete}
              >
                <Icon name={"delete"} size={18} />
                Delete Tag
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full text-sm"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
